/**
 * Schemas Zod das rotas de `/auth` (RNF-30, decisão A7).
 *
 * Fonte única: a API valida com estes schemas e o formulário do `apps/web`
 * importa os mesmos. Validação no cliente é conveniência; a garantia é a do
 * servidor.
 *
 * A política de senha **não** é replicada aqui em regex. Ela vive em
 * `seguranca/senha.ts` e é aplicada pelo caso de uso, que devolve
 * `422 SENHA_FRACA` com a lista completa de violações. Duplicar a regra num
 * `.regex()` criaria duas definições que divergem na primeira alteração.
 */
import { z } from 'zod'

import { SENHA_MAX_CARACTERES } from '../seguranca/senha'

/** E-mail normalizado: `citext` no banco resolve o caso, aqui só apara ruído. */
const email = z
  .string({ required_error: 'Informe o e-mail.' })
  .trim()
  .toLowerCase()
  .min(1, 'Informe o e-mail.')
  .max(320, 'E-mail muito longo.')
  .email('Informe um e-mail válido.')

/**
 * Senha na ENTRADA de login: só formato, nunca política. Aplicar a política no
 * login revelaria, pela mensagem de erro, que a senha digitada "não poderia ser"
 * a senha da conta — informação a mais para quem sonda (RN-01).
 */
const senhaLogin = z
  .string({ required_error: 'Informe a senha.' })
  .min(1, 'Informe a senha.')
  .max(SENHA_MAX_CARACTERES, 'Senha muito longa.')

/** Senha NOVA: comprimento e limite; o resto é a política do servidor. */
const senhaNova = z
  .string({ required_error: 'Informe a nova senha.' })
  .min(1, 'Informe a nova senha.')
  .max(SENHA_MAX_CARACTERES, 'Senha muito longa.')

const tokenSenha = z
  .string({ required_error: 'Link inválido.' })
  .trim()
  .min(20, 'Link inválido.')
  .max(200, 'Link inválido.')

// -----------------------------------------------------------------------------
// POST /auth/login
// -----------------------------------------------------------------------------
export const schemaLogin = z.object({
  email,
  senha: senhaLogin,
  /**
   * "Lembrar deste dispositivo". Não estende a validade do refresh (7 dias é
   * teto de segurança, RNF-11); só sinaliza que o cliente deve renovar em
   * segundo plano em vez de exigir novo login ao reabrir a aba.
   */
  lembrarDispositivo: z.boolean().optional().default(false),
})
export type EntradaLogin = z.infer<typeof schemaLogin>

// -----------------------------------------------------------------------------
// POST /auth/recuperar-senha
// -----------------------------------------------------------------------------
export const schemaRecuperarSenha = z.object({ email })
export type EntradaRecuperarSenha = z.infer<typeof schemaRecuperarSenha>

// -----------------------------------------------------------------------------
// POST /auth/redefinir-senha  (consome RECUPERACAO ou CONVITE)
// -----------------------------------------------------------------------------
export const schemaRedefinirSenha = z
  .object({
    token: tokenSenha,
    senha: senhaNova,
    confirmacaoSenha: z.string({ required_error: 'Confirme a nova senha.' }),
  })
  .refine((dados) => dados.senha === dados.confirmacaoSenha, {
    path: ['confirmacaoSenha'],
    message: 'As senhas não conferem.',
  })
export type EntradaRedefinirSenha = z.infer<typeof schemaRedefinirSenha>

// -----------------------------------------------------------------------------
// POST /auth/alterar-senha  (TELA-24, autenticado)
// -----------------------------------------------------------------------------
export const schemaAlterarSenha = z
  .object({
    senhaAtual: z
      .string({ required_error: 'Informe a senha atual.' })
      .min(1, 'Informe a senha atual.')
      .max(SENHA_MAX_CARACTERES, 'Senha muito longa.'),
    senhaNova,
    confirmacaoSenhaNova: z.string({ required_error: 'Confirme a nova senha.' }),
  })
  .refine((dados) => dados.senhaNova === dados.confirmacaoSenhaNova, {
    path: ['confirmacaoSenhaNova'],
    message: 'As senhas não conferem.',
  })
  .refine((dados) => dados.senhaNova !== dados.senhaAtual, {
    path: ['senhaNova'],
    message: 'A nova senha precisa ser diferente da atual.',
  })
export type EntradaAlterarSenha = z.infer<typeof schemaAlterarSenha>
